import React from "react";

import { Spinner, TextEditor } from "@performyard/py-components";

import NotesSwitcher from "./NotesSwitcher";

import type { NotesData } from "../types";

import { config } from "../config";
import { useMeetingContext } from "../context";

type PublicNotesSectionProps = {
	notesData: NotesData;
	isNotesLoading: boolean;
	isNotesDisabled: boolean;
	selectedParticipantId: string;
	publicNoteContent: string;
	showPublicNotesSaved: boolean;
	showPublicNotesSaving: boolean;
	isPublicNotesError: boolean;
	onParticipantSelect: (userId: string) => void;
	onPublicNoteChange: (value: string) => void;
};

/**
 * Public notes section with AvatarGroup to switch between participants' notes.
 * Displays the shared notes editor with autosave status.
 */
const PublicNotesSection = ({
	notesData,
	isNotesLoading,
	isNotesDisabled,
	selectedParticipantId,
	publicNoteContent,
	showPublicNotesSaved,
	showPublicNotesSaving,
	isPublicNotesError,
	onParticipantSelect,
	onPublicNoteChange,
}: PublicNotesSectionProps): React.ReactElement => {
	const { userId, isAdminView } = useMeetingContext();

	const isViewingOwnNotes = selectedParticipantId === userId && !isAdminView;
	const isReadOnly = isNotesDisabled || !isViewingOwnNotes;

	// When viewing own notes, use the autosave-managed content.
	// When viewing another participant's notes, look up from the participant notes map.
	const currentNoteValue = isViewingOwnNotes
		? publicNoteContent
		: (notesData.participantNotesMap.get(selectedParticipantId)?.content ?? "");

	return (
		<div className="flex flex-col gap-2">
			<NotesSwitcher
				selectedParticipantId={selectedParticipantId}
				onParticipantSelect={onParticipantSelect}
				showSaved={showPublicNotesSaved}
				showSaving={showPublicNotesSaving}
				hasError={isPublicNotesError}
			/>
			{isNotesLoading ? (
				<div className="flex items-center justify-center py-8">
					<Spinner size={config.spinnerSize.medium} />
				</div>
			) : (
				<TextEditor
					id="public-notes-editor"
					initialValue={currentNoteValue}
					onChange={onPublicNoteChange}
					placeholder={isViewingOwnNotes ? "Your notes here" : "No shared notes recorded"}
					readOnly={isReadOnly}
					showPlaceholderWhenReadOnly={true}
					minHeight={config.layout.textEditorMinHeight}
					maxHeight={config.layout.textEditorMaxHeight}
					// TextEditor's outer wrapper hard-codes `cursor-text` and only swaps to
					// `cursor-not-allowed` on `data-[disabled]`, not `data-[readonly]`. When we
					// drop into read-only mode (viewing another participant's notes or any
					// non-editable state), force the disabled cursor so the affordance matches
					// other disabled inputs in the app — including on the inner contenteditable
					// which has its own browser-default `cursor: text`.
					dangerouslySetClassName={isReadOnly ? "!cursor-not-allowed [&_*]:!cursor-not-allowed" : undefined}
				/>
			)}
		</div>
	);
};

export default PublicNotesSection;
